import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { publicRequest, userRequest } from "../api/requestMethods";

const initialState = {
  deliveryFee: 0,
  distanceKm: 0,
  distanceSource: null,
  estimatedMinutes: 0,
  feeBand: null,
  zone: null,
  breakdown: null,
  vendorName: null,
  // Signed quote token from POST /api/quote. Forwarded to the server on
  // order-create as `clientQuoteToken`; the server re-verifies it and, if still
  // valid, honours the exact fee it was shown. NEVER authoritative on the client.
  quoteToken: null,
  quoteExpiresAt: null,
  fulfillment: null,
  freightTermsAccepted: false,
  status: "idle",
  error: null,
};

export const fetchDeliveryFee = createAsyncThunk(
  "deliveryFee/fetchDeliveryFee",
  async ({ vendorId, customerLat, customerLng }) => {
    const response = await publicRequest.get(
      `/api/delivery-fee?vendorId=${vendorId}&customerLat=${customerLat}&customerLng=${customerLng}`
    );
    return response.data;
  }
);

// POST /api/quote → server-signed quote { fee, distanceKm, zone, breakdown,
// quoteToken, expiresAt }. The signed sibling of /api/delivery-fee and the single
// source the client uses to DISPLAY the fee and later prove (via quoteToken)
// which number it showed. Uses userRequest because /api/quote is auth-protected.
export const fetchDeliveryQuote = createAsyncThunk(
  "deliveryFee/fetchDeliveryQuote",
  async ({ vendorId, outletId, customerLat, customerLng, items, mode }, { rejectWithValue }) => {
    try {
      const response = await userRequest.post("/api/quote", {
        vendorId,
        // /api/quote resolves the OUTLET's own coords + radius when it is given
        // an outletId, and silently falls back to the parent vendor when it is
        // not. Order-create already prices from the outlet (it reads the id off
        // the order lines), so omitting it here meant the quoted fee and the
        // charged fee were measured from two different addresses.
        ...(outletId ? { outletId } : {}),
        dropoffCoords: { lat: customerLat, lng: customerLng },
        ...(Array.isArray(items) ? { items } : {}),
        ...(mode ? { mode } : {}),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || { message: error.message || "Failed to fetch quote" }
      );
    }
  }
);

const deliveryFeeSlice = createSlice({
  name: "deliveryFee",
  initialState,
  reducers: {
    resetDeliveryFee: () => {
      return initialState;
    },
    setFreightTermsAccepted: (state, action) => {
      state.freightTermsAccepted = action.payload === true;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchDeliveryFee.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchDeliveryFee.fulfilled, (state, action) => {
        state.deliveryFee = action.payload.deliveryFee || 0;
        state.distanceKm = action.payload.distanceKm || 0;
        state.distanceSource = action.payload.distanceSource || null;
        state.estimatedMinutes = action.payload.estimatedMinutes || 0;
        state.feeBand = action.payload.feeBand || null;
        state.zone = action.payload.zone || null;
        state.breakdown = action.payload.breakdown || null;
        state.vendorName = action.payload.vendorName || null;
        state.status = "succeeded";
      })
      .addCase(fetchDeliveryFee.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Quote endpoint — populates the same display fields PLUS the signed token.
      .addCase(fetchDeliveryQuote.pending, (state) => {
        state.status = "loading";
        state.freightTermsAccepted = false;
      })
      .addCase(fetchDeliveryQuote.fulfilled, (state, action) => {
        state.deliveryFee = action.payload.fee || 0;
        state.distanceKm = action.payload.distanceKm || 0;
        state.distanceSource = action.payload.distanceSource || null;
        state.estimatedMinutes = action.payload.estimatedDeliveryMinutes || 0;
        state.zone = action.payload.zone || null;
        state.breakdown = action.payload.breakdown || null;
        state.quoteToken = action.payload.quoteToken || null;
        state.quoteExpiresAt = action.payload.expiresAt || null;
        state.fulfillment = action.payload.fulfillment || null;
        state.status = "succeeded";
      })
      .addCase(fetchDeliveryQuote.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload?.message || action.error?.message || "Failed to fetch quote";
      });
  },
});

export default deliveryFeeSlice.reducer;

export const { resetDeliveryFee, setFreightTermsAccepted } = deliveryFeeSlice.actions;
